import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { DIALOG_TYPE } from '../../../../constants';
import { Button, EnhancedAccordian } from '../../../../ui';
import { AddQuestionModal } from '../../../../ui/add_question_modal/add_question_modal';
import { Loader } from '../../../../ui/loader/loader';
import { Modal } from '../../../../ui/modal/modal';
import { PaginationController } from '../../../../ui/pagination_controller/pagination_controller';
import { Permission } from '../../../../utils/helpers/permissions';
import PermissionGate from '../../../../utils/hooks/PermissionGate';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { SelectedQuestionContent } from '../../../job_openings/create/AssessmentQuestions/Containers/Questions/Containers/Components';
import { useQuestionBank } from '../Hooks';
import { QuestionsFilter } from './QUestionsFilter';

export const Questions: React.FC = () => {
  const {
    skillList,
    levels,
    subSkillsStore,
    jobTitles,
    searchObject,
    setSearchObject,
    searchTerm,
    setSearchTerm,
    setAddModalOpen,
    searchResults,
    onEditSave,
    deleteQuestionFromJob,
    setSkillList,
    expandedIds,
    toggleExpand,
    disabledMap,
    setDisabled,
    handleChange,
    handleSelectChange,
    handleMultiSelectChange,
    onAddNewSkill,
    questionTypes,
    pageNumber,
    totalPages,
    totalQuestions,
    switchPages,
    addModalOpen,
    addNewQuestion,
    loading,
    addFileViaInput,
    clearMediaFile,
  } = useQuestionBank();

  return (
    <>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', xl: 'row' },
            alignItems: { xl: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <QuestionsFilter
            skillList={skillList}
            levelList={levels.levelList}
            subSkillList={subSkillsStore.subSkillsList}
            jobTitleList={jobTitles.jobTitlesList}
            searchObject={searchObject}
            setSearchObject={setSearchObject}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            <TextField
              size="small"
              placeholder="Search Questions"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: { xs: '100%', sm: 240 }, '& .MuiInputBase-input': { py: 1, fontSize: '0.875rem' } }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <PermissionGate requiredPermissions={[Permission.QUESTIONS_CREATE]}>
                <Button
                  onClick={() => setAddModalOpen(true)}
                  variant="primary"
                  label="Create New Question"
                  icon={<Plus size={16} />}
                  hasIcon
                  sx={{ minHeight: 36, px: 1.5, py: 0.75, fontSize: '0.875rem' }}
                />
              </PermissionGate>
              <PermissionGate requiredPermissions={[Permission.QUESTIONS_DELETE]}>
                <Button
                  onClick={() => {}}
                  variant="secondary"
                  dialogType={DIALOG_TYPE.ERROR}
                  label="Delete"
                  icon={<Trash2 size={16} />}
                  hasIcon
                  sx={{ minHeight: 36, px: 1.5, py: 0.75, fontSize: '0.875rem' }}
                />
              </PermissionGate>
            </Stack>
          </Box>
        </Box>
      </Paper>

      <Stack spacing={1}>
        {searchResults.map((q) => {
          const selectedProps = {
            toggledQuestion: q,
            isDisabled: disabledMap[q.id] ?? true,
            setIsDisabled: (d: boolean) => setDisabled(q.id, d),
            editQuestion: onEditSave,
            handleChange,
            handleSelectChange,
            handleMultiSelectChange,
            onAddNewSkill,
            returnSelectedJobTitles: () =>
              (q.jobTitle ?? [])
                .map((id) =>
                  jobTitles.jobTitlesList.find((t) => t.value === id),
                )
                .filter((x): x is SelectOptions => Boolean(x))
                .map((opt) => ({ label: opt.label, value: opt.value })),
            addFileViaInput,
            clearMediaFile,
            toggleCollapsible: () => toggleExpand(q.id),
            skills: skillList,
            subSkills1: subSkillsStore.subSkillsList
              .filter((s) => s.parentSkillId === q.skillId)
              .map((s) => ({ label: s.subskillName, value: s.id })),
            subSkills2: subSkillsStore.subSkillsList
              .filter((s) => s.parentSkillId === q.subSkill1Id)
              .map((s) => ({ label: s.subskillName, value: s.id })),
            subSkills3: subSkillsStore.subSkillsList
              .filter((s) => s.parentSkillId === q.subSkill2Id)
              .map((s) => ({ label: s.subskillName, value: s.id })),
            levelList: levels.levelList,
            jobTitleList: jobTitles.jobTitlesList,
            questionTypes,
          };

          return (
            <EnhancedAccordian
              key={q.id}
              title={q.question}
              isExpanded={expandedIds.includes(q.id)}
              onToggle={() => toggleExpand(q.id)}
              rightIcons={[
                <IconButton
                  key="edit"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDisabled(q.id, false);
                    if (!expandedIds.includes(q.id)) toggleExpand(q.id);
                  }}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  <Pencil size={16} />
                </IconButton>,
                <IconButton
                  key="delete"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteQuestionFromJob(q.id);
                  }}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  <Trash2 size={16} />
                </IconButton>,
              ]}
            >
              <SelectedQuestionContent {...selectedProps} />
            </EnhancedAccordian>
          );
        })}
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          Total Questions:{' '}
          <Typography component="span" variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>
            {totalQuestions}
          </Typography>
        </Typography>
        <PaginationController
          pageNumber={pageNumber}
          maxPageNumber={totalPages}
          navigateNextPage={() => switchPages(pageNumber + 1)}
          navigatePreviousPage={() => switchPages(pageNumber - 1)}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Rows
          </Typography>
          <TextField
            size="small"
            name="questionPage"
            type="number"
            value={searchObject.questionPage}
            onChange={(e) =>
              setSearchObject({
                ...searchObject,
                [e.target.name]: parseInt(e.target.value, 10),
              })
            }
            sx={{ width: 72, '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.8125rem' } }}
          />
        </Box>
      </Paper>

      <Modal
        visible={addModalOpen}
        headerText="Create New Question"
        customClass="max-w-5xl"
        modalContent={
          <AddQuestionModal
            addNewQuestion={addNewQuestion}
            skillList={skillList}
            levelList={levels.levelList}
            jobTitleList={jobTitles.jobTitlesList}
            subSkillList={subSkillsStore.subSkillsList}
            setSkillList={setSkillList}
          />
        }
        toggle={() => setAddModalOpen(false)}
      />

      <Loader loading={loading} />
    </>
  );
};
